import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useChat } from '../hooks/useChat';
import { useSettings } from '../hooks/useSettings';
import ChatHistory from './ChatHistory';
import {
  MessageCircle,
  Trash2,
  Database,
  Activity,
  Wifi,
  WifiOff,
  BarChart3,
  MessageSquare,
  LogOut,
  User,
  AlertCircle,
  ChevronsLeft,
  ChevronsRight,
  PanelLeftClose,
  PanelLeftOpen
} from 'lucide-react';
import { useChatHistory } from '../hooks/useChatHistory';

const SidebarContainer = styled(motion.div)`
  width: ${props => props.$collapsed ? '72px' : '320px'};
  min-width: ${props => props.$collapsed ? '72px' : '320px'};
  height: 100vh;
  /* Sea and sky gradient inspired by King Fahd Causeway */
  background: linear-gradient(
    180deg,
    #0D5E5E 0%,           /* Deep teal sky */
    #1A8B8B 15%,          /* Lighter teal horizon */
    #2A9D9D 30%,          /* Bright teal */
    #1E7878 50%,          /* Sea meeting sky */
    #0F5555 70%,          /* Deep sea */
    #0A3D3D 85%,          /* Darker sea */
    #062D2D 100%          /* Ocean depths */
  );
  padding: ${props => props.$collapsed ? '24px 12px' : '24px'};
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-y: auto;
  overflow-x: visible;
  transition: width 0.3s ease, min-width 0.3s ease, padding 0.3s ease;

  /* RTL/LTR aware styling */
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
  box-shadow: ${props => props.$isRTL ? '-4px 0 20px rgba(0, 0, 0, 0.15)' : '4px 0 20px rgba(0, 0, 0, 0.15)'};

  /* Subtle wave pattern overlay */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(ellipse 80% 50% at 50% 100%, rgba(255,255,255,0.03) 0%, transparent 50%),
      radial-gradient(ellipse 60% 30% at 30% 80%, rgba(255,255,255,0.02) 0%, transparent 40%),
      radial-gradient(ellipse 60% 30% at 70% 85%, rgba(255,255,255,0.02) 0%, transparent 40%);
    pointer-events: none;
  }
`;

const CollapseToggle = styled(motion.button)`
  position: absolute;
  top: 12px;
  ${props => props.$isRTL ? 'left: 12px;' : 'right: 12px;'}
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.05);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  opacity: ${props => props.$collapsed ? 0 : 1};
  visibility: ${props => props.$collapsed ? 'hidden' : 'visible'};
  transition: opacity 0.2s ease, visibility 0.2s ease;
`;

const CollapsedContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding-top: 40px;
  opacity: ${props => props.$collapsed ? 1 : 0};
  visibility: ${props => props.$collapsed ? 'visible' : 'hidden'};
  position: ${props => props.$collapsed ? 'relative' : 'absolute'};
  transition: opacity 0.2s ease, visibility 0.2s ease;
`;

const CollapsedIcon = styled(motion.button)`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  position: relative;
  padding: 12px;
  z-index: 1;
`;

const Logo = styled.img`
  height: 75px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  /* Logo is now transparent PNG - no filter needed */
  transition: transform 0.3s ease, opacity 0.3s ease;

  &:hover {
    transform: scale(1.02);
    opacity: 0.95;
  }
`;

const TabNav = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  padding: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
`;

const TabButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.25)' : 'transparent'};
  color: white;
  font-weight: ${props => props.$active ? '600' : '500'};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$active ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)'};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
`;

const DatabaseSection = styled.div`
  margin-bottom: 24px;
`;

const SectionLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: white;
  margin-bottom: 8px;
`;

const DatabaseSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  appearance: none;
  backdrop-filter: blur(10px);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1);
  }

  option {
    background: #0D1B2A;
    color: white;
    padding: 12px;
  }
`;

const SelectWrapper = styled.div`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    ${props => props.$isRTL ? 'left: 16px;' : 'right: 16px;'}
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid white;
    pointer-events: none;
    opacity: 0.7;
  }
`;

const DatabaseInfo = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
`;

const ActionButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const StatusSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin-bottom: 8px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ConnectionStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  margin-bottom: 16px;

  .status-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.$connected ? '#1D7A5F' : '#C53030'};
    animation: ${props => props.$connected ? 'pulse 2s infinite' : 'none'};
  }

  .status-text {
    color: white;
    font-size: 14px;
    font-weight: 500;
  }

  svg {
    width: 16px;
    height: 16px;
    color: white;
  }
`;

const ProcessingIndicator = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  background: rgba(251, 191, 36, 0.2);
  border: 1px solid rgba(251, 191, 36, 0.3);
  margin-bottom: 16px;

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top: 2px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .text {
    color: white;
    font-size: 12px;
    font-weight: 500;
  }
`;

const MessageCount = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  text-align: center;
  margin-top: 16px;
`;

const SecondaryNav = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const SecondaryButton = styled(motion.button)`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const UserSection = styled.div`
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
  font-size: 14px;
  margin-bottom: 12px;
  padding: 8px 0;

  svg {
    width: 18px;
    height: 18px;
    opacity: 0.9;
  }
`;

const LogoutButton = styled(motion.button)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Database options with descriptions - KFC Toll Transactions
const DATABASE_OPTIONS = [
  {
    id: 'kfc_postgres',
    name: 'KFCA Toll (PostgreSQL)',
    description: 'قاعدة بيانات معاملات رسوم المرور على جسر الملك فهد - تحتوي على بيانات شاملة لجميع العبور والمعاملات'
  }
];

function Sidebar({ activeTab, setActiveTab, username, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);

  const {
    messages,
    isProcessing,
    processingStep,
    connected,
    dbId,
    actions
  } = useChat();

  const { setCurrentSessionId } = useChatHistory();

  const { isRTL, language } = useSettings();

  const toggleCollapse = () => setCollapsed(!collapsed);

  // Handle session selection from chat history
  const handleSelectSession = (session) => {
    if (session) {
      // Convert database messages to chat format and load into useChat
      // Must include ALL fields that createMessage() + final_result.data spreading provides
      // to ensure identical rendering between real-time and loaded messages
      // IMPORTANT: Always set messages, even if empty (for new sessions)
      const chatMessages = (session.messages || []).map(msg => ({
        id: msg.id,
        content: msg.content,
        isUser: msg.role === 'user',
        timestamp: new Date(msg.created_at).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }),
        // IMPORTANT: final_answer must be set for bot messages to match real-time rendering
        // In real-time, final_result.data.final_answer is spread onto the message
        final_answer: msg.role === 'assistant' ? msg.content : undefined,
        // SQL and data
        sql_query: msg.sql_query,
        results: msg.metadata?.results,
        column_names: msg.metadata?.column_names,
        // Visualization
        visualization: msg.chart_spec,
        // Explanation
        explanation: msg.metadata?.explanation,
        // Follow-up suggestions
        suggestions: msg.metadata?.suggestions,
        // Reformulation info
        reformulation: msg.metadata?.reformulation,
        // Context for multi-turn
        context: msg.metadata?.context
      }));

      // Load messages into chat state (clears old messages for new sessions)
      actions.setMessages(chatMessages);
      setCurrentSessionId(session.id);
    }
  };

  const buttonVariants = {
    hover: { scale: 1.02 },
    tap: { scale: 0.98 }
  };

  const handleDatabaseChange = (event) => {
    const newDbId = event.target.value;
    actions.setDbId(newDbId);
  };

  const getCurrentDatabaseInfo = () => {
    return DATABASE_OPTIONS.find(db => db.id === dbId) || DATABASE_OPTIONS[0];
  };

  return (
    <SidebarContainer
      $isRTL={isRTL}
      $collapsed={collapsed}
      initial={{ x: isRTL ? 300 : -300 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* Collapse Toggle Button */}
      <CollapseToggle
        $isRTL={isRTL}
        onClick={toggleCollapse}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        title={collapsed ? (language === 'ar' ? 'توسيع' : 'Expand') : (language === 'ar' ? 'طي' : 'Collapse')}
      >
        {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
      </CollapseToggle>

      {/* Collapsed View - Icon buttons only */}
      {collapsed && (
        <CollapsedContent $collapsed={collapsed}>
          <CollapsedIcon
            $active={activeTab === 'chat'}
            onClick={() => setActiveTab('chat')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={language === 'ar' ? 'المحادثة' : 'Chat'}
          >
            <MessageSquare />
          </CollapsedIcon>
          <CollapsedIcon
            $active={activeTab === 'dashboard'}
            onClick={() => setActiveTab('dashboard')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={language === 'ar' ? 'لوحة المعلومات' : 'Dashboard'}
          >
            <BarChart3 />
          </CollapsedIcon>
          <CollapsedIcon
            $active={activeTab === 'feedback'}
            onClick={() => setActiveTab('feedback')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={language === 'ar' ? 'الإبلاغ' : 'Feedback'}
          >
            <AlertCircle />
          </CollapsedIcon>
          <CollapsedIcon
            $active={activeTab === 'debug'}
            onClick={() => setActiveTab('debug')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={language === 'ar' ? 'التتبع' : 'Debug'}
          >
            <Activity />
          </CollapsedIcon>
          <div style={{ flex: 1 }} />
          <CollapsedIcon
            onClick={onLogout}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={language === 'ar' ? 'تسجيل الخروج' : 'Logout'}
          >
            <LogOut />
          </CollapsedIcon>
        </CollapsedContent>
      )}

      {/* Full Sidebar Content */}
      <SidebarContent $collapsed={collapsed}>
        <Header>
          <LogoContainer>
            <Logo src="/kfca-logo.svg" alt="King Fahd Causeway Authority" />
          </LogoContainer>
          <Title>
            <MessageCircle />
            {language === 'ar' ? 'نظام رسوم المرور' : 'Toll System'}
          </Title>
          <Subtitle>
            {language === 'ar'
              ? 'اطرح أسئلة حول معاملات المرور بلغة طبيعية واحصل على رؤى فورية مع تصورات جميلة.'
              : 'Ask questions about toll transactions in natural language and get instant insights with beautiful visualizations.'}
          </Subtitle>
        </Header>

        <TabNav>
          <TabButton
            $active={activeTab === 'chat'}
            onClick={() => setActiveTab('chat')}
          >
            <MessageSquare />
            {language === 'ar' ? 'المحادثة' : 'Chat'}
          </TabButton>
          <TabButton
            $active={activeTab === 'dashboard'}
            onClick={() => setActiveTab('dashboard')}
          >
            <BarChart3 />
            {language === 'ar' ? 'لوحة المعلومات' : 'Dashboard'}
          </TabButton>
        </TabNav>

        {/* Chat History - only show on chat tab */}
        {activeTab === 'chat' && (
          <ChatHistory onSelectSession={handleSelectSession} />
        )}

        <DatabaseSection>
          <SectionLabel>
            <Database style={{ display: 'inline', marginInlineStart: '8px', width: '16px', height: '16px' }} />
            {language === 'ar' ? 'اختر قاعدة البيانات' : 'Select Database'}
          </SectionLabel>
          <SelectWrapper $isRTL={isRTL}>
            <DatabaseSelect
              value={dbId}
              onChange={handleDatabaseChange}
              disabled={isProcessing}
            >
              {DATABASE_OPTIONS.map(db => (
                <option key={db.id} value={db.id}>
                  {db.name}
                </option>
              ))}
            </DatabaseSelect>
          </SelectWrapper>
          <DatabaseInfo>
            {getCurrentDatabaseInfo().description}
          </DatabaseInfo>
        </DatabaseSection>

        {/* Clear Chat button - only show on chat tab */}
        {activeTab === 'chat' && (
          <ButtonGroup>
            <ActionButton
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={actions.clearMessages}
              disabled={isProcessing || messages.length === 0}
            >
              <Trash2 />
              {language === 'ar' ? 'مسح المحادثة' : 'Clear Chat'}
            </ActionButton>
          </ButtonGroup>
        )}

        <StatusSection>
          {isProcessing && (
            <ProcessingIndicator
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="spinner" />
              <div className="text">{processingStep || (language === 'ar' ? 'جاري المعالجة...' : 'Processing...')}</div>
            </ProcessingIndicator>
          )}

          <ConnectionStatus $connected={connected}>
            <div className="status-indicator" />
            {connected ? <Wifi /> : <WifiOff />}
            <div className="status-text">
              {connected
                ? (language === 'ar' ? 'متصل' : 'Connected')
                : (language === 'ar' ? 'غير متصل' : 'Disconnected')}
            </div>
          </ConnectionStatus>

          <StatusItem>
            <Database />
            {language === 'ar' ? 'قاعدة البيانات:' : 'Database:'} {getCurrentDatabaseInfo().name}
          </StatusItem>

          <StatusItem>
            <Activity />
            {connected
              ? (language === 'ar' ? 'جاهز' : 'Ready')
              : (language === 'ar' ? 'غير متصل' : 'Disconnected')}
          </StatusItem>

          <MessageCount>
            {language === 'ar'
              ? `${messages.length} ${messages.length === 1 ? 'رسالة' : messages.length === 2 ? 'رسالتان' : 'رسائل'} في المحادثة`
              : `${messages.length} message${messages.length === 1 ? '' : 's'} in chat`}
          </MessageCount>
        </StatusSection>

        <SecondaryNav>
          <SecondaryButton
            $active={activeTab === 'feedback'}
            onClick={() => setActiveTab('feedback')}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <AlertCircle />
            {language === 'ar' ? 'الإبلاغ عن مشاكل الاستعلامات' : 'Report Query Issues'}
          </SecondaryButton>
          <SecondaryButton
            $active={activeTab === 'debug'}
            onClick={() => setActiveTab('debug')}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <Activity />
            {language === 'ar' ? 'لوحة التتبع والتصحيح' : 'Debug Dashboard'}
          </SecondaryButton>
        </SecondaryNav>

        <UserSection>
          <UserInfo>
            <User />
            <span>{username || (language === 'ar' ? 'مستخدم' : 'User')}</span>
          </UserInfo>
          <LogoutButton
            onClick={onLogout}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <LogOut />
            {language === 'ar' ? 'تسجيل الخروج' : 'Logout'}
          </LogoutButton>
        </UserSection>
      </SidebarContent>
    </SidebarContainer>
  );
}

export default Sidebar; 